// src/app/core/auth/guards/demo.guard.ts
import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { AuthService } from '../auth.service';
import { switchMap } from 'rxjs/operators';
import { LocalStorageService } from '../../services/local-storage.service';
import { LocalStorageKeys } from '../../constants/local-storage-keys.enum';
import { AgencyStateService } from '../../services/agency-state.service';

@Injectable({
  providedIn: 'root'
})
export class DemoGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private localStorage: LocalStorageService,
    private agencyState: AgencyStateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const isDemoPath = state.url.startsWith('/demo');

    // Якщо це демо-шлях
    if (isDemoPath) {
      const detectedType = this.detectDemoType(route, state);

      // Якщо ще не в демо-режимі, активуємо його з правильним типом.
      // Priority: explicit ?type= query → /demo/agency URL prefix → stored
      // localStorage[DEMO_TYPE] → 'brand' fallback. Keeps deep-link entry to
      // /demo/agency/dashboard from accidentally logging in as brand.
      if (!this.authService.isDemoMode()) {
        return this.loginAndHydrate(detectedType);
      }

      // Already in demo. Detect a type mismatch — e.g. user is on a brand
      // demo session but deep-linked into /demo/agency/*. The previous
      // bare `return of(true)` here would hand them off to the agency
      // routes with a brand JWT, which breaks every agency-only endpoint
      // and leaves the UI half-rendered. Force a re-login with the new
      // type when the URL prefix mandates one.
      const storedType = this.localStorage.get<string>(LocalStorageKeys.DEMO_TYPE);
      const requiresAgency = state.url.startsWith('/demo/agency');
      const requiresBrand =
        state.url.startsWith('/demo/dashboard') ||
        (state.url.startsWith('/demo/') && !state.url.startsWith('/demo/agency'));
      const mismatch =
        (requiresAgency && storedType !== 'agency') || (requiresBrand && storedType === 'agency');

      if (mismatch) {
        return this.loginAndHydrate(detectedType);
      }

      return of(true);
    }

    // Якщо не демо-шлях, але активовано демо-режим,
    // перенаправляємо на відповідний демо-шлях
    if (this.authService.isDemoMode()) {
      const demoUrl = '/demo' + (state.url.startsWith('/') ? state.url : '/' + state.url);
      this.router.navigateByUrl(demoUrl, { replaceUrl: true });
      return of(false);
    }

    // Стандартний випадок - не демо-шлях і не демо-режим
    return of(true);
  }

  private detectDemoType(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): 'brand' | 'agency' {
    const queryType = route.queryParamMap.get('type');
    if (queryType === 'agency' || queryType === 'brand') {
      return queryType;
    }
    if (state.url.startsWith('/demo/agency')) {
      return 'agency';
    }
    const stored = this.localStorage.get<string>(LocalStorageKeys.DEMO_TYPE);
    if (stored === 'agency' || stored === 'brand') {
      return stored;
    }
    return 'brand';
  }

  /**
   * Performs a fresh `demoLogin(type)` and (only for agency) hydrates
   * `AgencyStateService` before resolving. Used both for first-time entry
   * and for type mismatch (brand session ↔ agency URL or vice versa).
   * `demoLogin` saves the new token + DEMO_TYPE in localStorage; the prior
   * brand JWT is discarded, so subsequent agency API calls go with the
   * right credentials.
   *
   * `clear()` runs BEFORE `demoLogin` so the cached `_initPromise` from the
   * previous demo session doesn't survive across the token swap. Without
   * this, switching agency→brand via deep-link leaves `isAgency = true` /
   * stale agency name in the sidebar even after `fetchAndUpdate()` runs —
   * the dedup short-circuits and never reissues `/users/me`. No-op on first
   * entry when no prior session exists.
   */
  private loginAndHydrate(type: 'brand' | 'agency'): Observable<boolean> {
    this.agencyState.clear();
    return this.authService.demoLogin(type).pipe(
      switchMap(response => {
        if (!response || !response.accessToken) {
          return of(false);
        }
        if (type === 'agency') {
          return from(this.agencyState.fetchAndUpdate()).pipe(switchMap(() => of(true)));
        }
        return of(true);
      })
    );
  }
}
